import { Layer } from "../types/Editor";
import { Input } from "./ui/input";
import { Player, PlayerRef } from "@remotion/player";
import { Upload, Target, Loader2, Play, Sparkles } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import VideoWithEffectsWrapper from "./VideoWithEffectsWrapper";

interface PreviewAreaProps {
  videoSrc: string | null;
  layers: Layer[];
  isPlaying: boolean;
  playerRef: React.RefObject<PlayerRef>;
  currentTool: "highlight" | "zoom" | "blur" | null;
  isDrawing: boolean;
  activeLayer: Layer | null;
  cursorPosition: { x: number; y: number } | null;
  videoAspectRatio: number;
  videoWidth: number;
  videoHeight: number;
  videoDuration: number;
  previewRef: React.RefObject<HTMLDivElement>;
  onMouseDown: (e: React.MouseEvent<HTMLDivElement>) => void;
  onMouseMove: (e: React.MouseEvent<HTMLDivElement>) => void;
  onMouseUp: () => void;
  onMouseLeave: () => void;
  onVideoUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onLayerUpdate: (layer: Layer) => void;
  setIsPlaying: (isPlaying: boolean) => void;
  setCurrentTime: (time: number) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
}

export function PreviewArea({
  videoSrc,
  layers,
  isPlaying,
  playerRef,
  currentTool,
  isDrawing,
  activeLayer,
  cursorPosition,
  videoAspectRatio,
  videoWidth,
  videoHeight,
  videoDuration,
  previewRef,
  onMouseDown,
  onMouseMove,
  onMouseUp,
  onMouseLeave,
  onVideoUpload,
  onLayerUpdate,
  setIsPlaying,
  setCurrentTime,
  fileInputRef,
}: PreviewAreaProps) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);

  useEffect(() => {
    function handleFullscreenChange() {
      const fullscreenElement =
        document.fullscreenElement || (document as any).webkitFullscreenElement;
      setIsFullscreen(!!fullscreenElement);
    }

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
    };
  }, []);

  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;

    const onWaiting = () => {
      setIsBuffering(true);
    };

    const onPlaying = () => {
      setIsBuffering(false);
    };

    player.addEventListener("waiting", onWaiting);
    player.addEventListener("play", onPlaying);
    player.addEventListener("resume", onPlaying);
    player.addEventListener("seeked", onPlaying);
    player.addEventListener("pause", onPlaying);

    return () => {
      player.removeEventListener("waiting", onWaiting);
      player.removeEventListener("play", onPlaying);
      player.removeEventListener("resume", onPlaying);
      player.removeEventListener("seeked", onPlaying);
      player.removeEventListener("pause", onPlaying);
    };
  }, [playerRef, videoSrc]);



  return (
    <div
      ref={previewRef}
      className="w-full h-full relative flex items-center justify-center bg-slate-900 overflow-hidden"
    >
      {videoSrc ? (
        <>
          <Player
            ref={playerRef}
            component={VideoWithEffectsWrapper as any}
            inputProps={{
              videoSrc,
              layers: isDrawing
                ? layers.filter((l) => l.id !== activeLayer?.id)
                : layers,
              isPaused: !isPlaying,
              onLayerUpdate,
              activeLayerId: activeLayer?.id,
              isDrawing,
              onMouseDown,
              onMouseMove,
              onMouseUp,
              onMouseLeave,
              activeLayer: isDrawing ? activeLayer : null,
              cursorPosition,
              currentTool,
            }}
            durationInFrames={Math.max(1, Math.ceil(videoDuration * 30))}
            compositionWidth={videoWidth || 1920}
            compositionHeight={videoHeight || 1080}
            fps={30}
            style={{
              width: "100%",
              height: "100%",
            }}
            autoPlay={isPlaying}
            acknowledgeRemotionLicense
            controls={isFullscreen}
            bufferStateDelayInMilliseconds={0}
          />

          {isBuffering && (
            <div className="absolute top-6 right-6 flex items-center gap-3 bg-white/95 backdrop-blur-md px-4 py-2 rounded-2xl z-50 pointer-events-none shadow-2xl border border-slate-100">
              <Loader2 className="h-4 w-4 text-indigo-600 animate-spin" />
              <span className="text-slate-900 text-xs font-bold uppercase tracking-wider">
                Syncing...
              </span>
            </div>
          )}

          {/* Zoom Tool Instruction Overlay */}
          {currentTool === "zoom" && !isDrawing && (
            <div className="absolute inset-x-0 bottom-10 pointer-events-none flex items-center justify-center z-40">
              <div className="bg-white/90 backdrop-blur-xl text-slate-900 px-8 py-4 rounded-[24px] shadow-2xl max-w-sm text-center border border-slate-200">
                <Target size={32} className="mx-auto mb-3 text-indigo-600 animate-pulse" />
                <h4 className="text-base font-bold mb-1">Focus Point</h4>
                <p className="text-xs font-medium text-slate-500 leading-relaxed px-2">
                  Click where you want the lens to focus. You can refine the center in the settings panel.
                </p>
              </div>
            </div>
          )}


        </>
      ) : (
        <div className="text-center p-12 bg-white rounded-[40px] shadow-2xl shadow-indigo-100 border border-slate-100 max-w-md w-full relative group">
          <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-24 h-24 bg-indigo-600 rounded-[32px] shadow-2xl shadow-indigo-200 flex items-center justify-center transform rotate-6 transition-transform group-hover:rotate-0">
            <Play size={40} className="text-white fill-white ml-2" />
          </div>

          <div className="mt-10">
            <h3 className="text-2xl font-black text-slate-900 tracking-tight mb-2">Ready to Create?</h3>
            <p className="text-slate-400 font-medium mb-10 px-6">Upload your raw video to start adding effects and zooms.</p>

            <label htmlFor="video-upload" className="cursor-pointer block">
              <div className="flex items-center justify-center gap-3 bg-indigo-600 hover:bg-indigo-700 text-white py-4 rounded-2xl font-bold text-lg transition-all shadow-xl shadow-indigo-100">
                <Upload size={20} />
                Import
              </div>
              <Input
                id="video-upload"
                type="file"
                accept="video/*"
                className="hidden"
                onChange={onVideoUpload}
                ref={fileInputRef}
              />
            </label>
          </div>
        </div>
      )}
    </div>
  );
}
